import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Search, ShoppingBag, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { Logo } from "./Logo";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Delivery", to: "/delivery" },
  { label: "How to Order", to: "/how-to-order" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
];

export function Header() {
  const { count, setDrawerOpen } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    api.categories().then((d) => setCategories(d.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const topLevel = categories.filter((c) => !c.parentId);
  const childrenOf = (id) => categories.filter((c) => c.parentId === id);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setMenuOpen(false);
    }
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 glass">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-10">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          <Link to="/shop" data-testid="nav-shop" className="text-sm font-medium text-brand-ink/80 transition-colors hover:text-brand-pine">
            Shop
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setCatsOpen(true)}
            onMouseLeave={() => setCatsOpen(false)}
          >
            <button
              data-testid="nav-categories"
              className="flex items-center gap-1 text-sm font-medium text-brand-ink/80 transition-colors hover:text-brand-pine"
              aria-expanded={catsOpen}
              aria-haspopup="true"
            >
              Categories <ChevronDown className={`h-3.5 w-3.5 transition-transform ${catsOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {catsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-full w-[34rem] pt-3"
                  data-testid="categories-dropdown"
                >
                  <div className="grid grid-cols-2 gap-1 rounded-2xl border border-brand-line bg-white p-4 shadow-xl shadow-brand-forest/10">
                    {topLevel.map((c) => (
                      <div key={c.id} className="p-1">
                        <Link
                          to={`/category/${c.id}`}
                          data-testid={`nav-category-${c.slug}`}
                          onClick={() => setCatsOpen(false)}
                          className="font-display text-sm font-bold text-brand-forest hover:text-brand-golddeep"
                        >
                          {c.name}
                        </Link>
                        <div className="mt-1 flex flex-col gap-0.5">
                          {childrenOf(c.id).map((s) => (
                            <Link
                              key={s.id}
                              to={`/category/${s.id}`}
                              data-testid={`nav-subcategory-${s.slug}`}
                              onClick={() => setCatsOpen(false)}
                              className="text-xs text-brand-ink/60 transition-colors hover:text-brand-pine"
                            >
                              {s.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV.slice(1).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-brand-ink/80 transition-colors hover:text-brand-pine"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <form onSubmit={submitSearch} className="hidden items-center md:flex" role="search">
            <AnimatePresence>
              {searchOpen && (
                <motion.input
                  ref={searchRef}
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 220, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search the menu…"
                  data-testid="header-search-input"
                  className="mr-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm outline-none focus:border-brand-gold"
                  aria-label="Search products"
                />
              )}
            </AnimatePresence>
            <button
              type="button"
              data-testid="search-toggle"
              aria-label="Search"
              onClick={() => (searchOpen ? submitSearch(new Event("submit")) : setSearchOpen(true))}
              className="rounded-full p-2.5 text-brand-forest transition-colors hover:bg-brand-pine/5"
            >
              <Search className="h-5 w-5" />
            </button>
          </form>
          <button
            data-testid="mobile-search-link"
            aria-label="Search"
            onClick={() => navigate("/search")}
            className="rounded-full p-2.5 text-brand-forest md:hidden"
          >
            <Search className="h-5 w-5" />
          </button>
          <button
            data-testid="cart-button"
            aria-label={`Open cart, ${count} items`}
            onClick={() => setDrawerOpen(true)}
            className="relative rounded-full p-2.5 text-brand-forest transition-colors hover:bg-brand-pine/5"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-gold text-[10px] font-bold text-brand-forest"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className="rounded-full p-2.5 text-brand-forest lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-brand-forest text-brand-cream lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-4">
              <Logo light />
              <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="p-2.5">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 pt-8" aria-label="Mobile">
              {[{ label: "Shop All", to: "/shop" }, ...NAV.slice(1), { label: "Contact", to: "/contact" }].map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setMenuOpen(false)}
                    className="block border-b border-white/10 py-4 font-display text-3xl font-bold tracking-tight"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-6">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-cream/50">Categories</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {topLevel.map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.id}`}
                      data-testid={`mobile-nav-category-${c.slug}`}
                      onClick={() => setMenuOpen(false)}
                      className="rounded-full border border-white/20 px-4 py-2 text-sm"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
